// Cave84 -- Market Prices hooks (Phase 4 Week 13)
// TanStack Query wrappers for services/marketPrices.ts

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { MarketPriceHistory, Vintage } from '@cave84/shared-types';
import {
  enqueuePriceRefresh,
  forceRequeuePriceRefresh,
  getLatestPrice,
  listPriceHistory,
  recordManualPrice,
  resolveReviewEntry,
  type PriceRefreshScope,
  type ReviewDecision,
} from '@/services/marketPrices';
import { qk } from '@/services/firestore';
import { listAllEntriesByType } from '@/services/generationQueue';
import type { GenerationQueueEntry } from '@cave84/shared-types';

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export function usePriceHistory(
  vintageId: string | undefined
): UseQueryResult<MarketPriceHistory[]> {
  return useQuery({
    queryKey: vintageId
      ? qk.marketPrices.byVintage(vintageId)
      : qk.marketPrices.byVintage('__noop__'),
    queryFn: () => (vintageId ? listPriceHistory(vintageId) : Promise.resolve([])),
    enabled: !!vintageId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useLatestPrice(
  vintageId: string | undefined
): UseQueryResult<MarketPriceHistory | null> {
  return useQuery({
    queryKey: vintageId
      ? qk.marketPrices.latestByVintage(vintageId)
      : qk.marketPrices.latestByVintage('__noop__'),
    queryFn: () => (vintageId ? getLatestPrice(vintageId) : Promise.resolve(null)),
    enabled: !!vintageId,
    staleTime: 5 * 60 * 1000,
  });
}

// BUG FIX (2026-05-23): was calling listPendingEntries which filters
// status IN ['pending','processing'] -- excludes needs_owner_review entirely.
// Fix: use listAllEntriesByType and filter client-side.
export function usePriceReviewQueue(): UseQueryResult<GenerationQueueEntry[]> {
  return useQuery({
    queryKey: ['generationQueue', 'needs_review', 'market_price_refresh'],
    queryFn: async () => {
      const all = await listAllEntriesByType('market_price_refresh', 5000);
      return all.filter((e) => e.status === 'needs_owner_review');
    },
    staleTime: 0,
  });
}

// All market_price_refresh jobs (all statuses) -- for wizard progress view.
export function usePriceRefreshQueue(): UseQueryResult<GenerationQueueEntry[]> {
  return useQuery({
    queryKey: ['generationQueue', 'all', 'market_price_refresh'],
    queryFn: () => listAllEntriesByType('market_price_refresh'),
    staleTime: 30 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useEnqueuePriceRefresh(): UseMutationResult<
  number,
  Error,
  {
    vintages: Vintage[];
    scope: PriceRefreshScope;
    wineRegionMap: Map<string, string>;
    wineProducerMap: Map<string, string>;
    priority?: 'high' | 'normal';
  }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ vintages, scope, wineRegionMap, wineProducerMap, priority }) =>
      enqueuePriceRefresh(vintages, scope, wineRegionMap, wineProducerMap, priority),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['generationQueue'] });
    },
  });
}

// Self-heal: clears stuck pending/processing queue entries for the given
// vintages and writes fresh priority='high' ones. Use when the "Wines
// without any price" list shows items stuck on "Queued…" indefinitely.
export function useForceRequeuePriceRefresh(): UseMutationResult<number, Error, string[]> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vintageIds: string[]) => forceRequeuePriceRefresh(vintageIds),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['generationQueue'] });
    },
  });
}

export function useRecordManualPrice(): UseMutationResult<
  string,
  Error,
  { vintageId: string; price: number; currency?: MarketPriceHistory['currency'] }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ vintageId, price, currency }) =>
      recordManualPrice(vintageId, price, currency),
    onSuccess: (_id, { vintageId }) => {
      void qc.invalidateQueries({ queryKey: qk.marketPrices.byVintage(vintageId) });
      void qc.invalidateQueries({ queryKey: qk.marketPrices.latestByVintage(vintageId) });
    },
  });
}

export function useResolveReviewEntry(): UseMutationResult<
  void,
  Error,
  { queueEntryId: string; vintageId: string; decision: ReviewDecision }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ queueEntryId, vintageId, decision }) =>
      resolveReviewEntry(queueEntryId, vintageId, decision),
    onSuccess: (_data, { vintageId }) => {
      void qc.invalidateQueries({
        queryKey: ['generationQueue', 'needs_review', 'market_price_refresh'],
      });
      void qc.invalidateQueries({ queryKey: qk.marketPrices.byVintage(vintageId) });
      void qc.invalidateQueries({ queryKey: qk.marketPrices.latestByVintage(vintageId) });
    },
  });
}
